import { Box } from '@telus-uds/components-web'
import { useRouter } from 'next/router'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useRuntimeAssetPrefix } from 'src/siteBuilder/utils/getRuntimeAssetPrefix'
import { SCROLL_ANCHOR_HASH, useScrollToAnchor } from './useScrollToAnchor'

interface RiverflowProps {
  deviceName: string
}

interface MessageData {
  type: string
  height?: number
}

const MIN_HEIGHT = 400

const AppleRiverflow = ({ deviceName }: RiverflowProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const assetPrefix = useRuntimeAssetPrefix()
  const { query } = useRouter()

  // `device` on the page's own URL (e.g. from a marketing link) preselects a model in the
  // compare dropdown further down the iframe chain. It's forwarded as `compareDevice` here
  // because `device` on this iframe's own src already names the Contentful export slug.
  const compareDevice = Array.isArray(query.device) ? query.device[0] : query.device

  // The CDN is a different origin than the page, so the content height cannot be read from
  // contentDocument — viewer.html measures the Apple export and reports it via postMessage.
  const iframeSrc = `${assetPrefix}/riverflow_data/viewer.html?device=${encodeURIComponent(deviceName)}${
    compareDevice ? `&compareDevice=${encodeURIComponent(compareDevice)}` : ''
  }`

  useScrollToAnchor(iframeRef, iframeSrc)

  // Only ever called from inside the effects below (never during render), so it's safe to read
  // `window` here despite this component also rendering on the server.
  const getExpectedOrigin = () => new URL(iframeSrc, window.location.href).origin

  // loading="lazy" would otherwise deadlock a direct deep link to SCROLL_ANCHOR_HASH: the
  // browser won't fetch the iframe until it's near the viewport, but it can't be scrolled into
  // view until it has loaded and reported the anchor position. Force an eager load in that case.
  useLayoutEffect(() => {
    if (window.location.hash === SCROLL_ANCHOR_HASH && iframeRef.current) {
      iframeRef.current.setAttribute('loading', 'eager')
    }
  }, [])

  useEffect(() => {
    const expectedOrigin = getExpectedOrigin()

    const handleMessage = (event: MessageEvent<MessageData>) => {
      const iframe = iframeRef.current
      if (!iframe || event.source !== iframe.contentWindow || event.origin !== expectedOrigin) return

      if (event.data?.type === 'resize' && typeof event.data.height === 'number' && event.data.height > 0) {
        iframe.style.height = `${Math.max(event.data.height, MIN_HEIGHT)}px`
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [iframeSrc])

  // viewer.html's own ResizeObserver can miss layout shifts triggered by the host page resizing
  // across breakpoints (e.g. no DOM mutation, just a media/container query reflow). Ask it to
  // re-measure explicitly whenever the window resizes, rather than relying on that alone.
  useEffect(() => {
    const expectedOrigin = getExpectedOrigin()
    let resizeTimeoutId: ReturnType<typeof setTimeout>

    const handleResize = () => {
      clearTimeout(resizeTimeoutId)
      resizeTimeoutId = setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage({ type: 'getHeight' }, expectedOrigin)
      }, 200)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(resizeTimeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [iframeSrc])

  return (
    <Box>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        style={{
          width: '100%',
          border: 'none',
          minHeight: `${MIN_HEIGHT}px`,
          transition: 'height 0.2s ease-out',
        }}
        title="Riverflow Preview"
        loading="lazy"
      />
    </Box>
  )
}

export default AppleRiverflow
